import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { crmObjectListInfiniteQueryOptions } from '@/lib/crm/store/hooks';
import {
  type PrefetchHandlers,
  usePrefetchOnIntent,
} from '@/lib/hooks/usePrefetchOnIntent';
import { fetchWithDefaults } from '@/lib/http/client';
import type { CrmObjectFilters } from '@/types/crm';

import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '../../core/SchemaClient';
import type { ColumnConfig, EntityTypeName } from '../../core/types';
import { entityTypesQueryKey } from '../../hooks/useEntityTypeSchema';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import {
  deriveVisibleColumnAssociations,
  deriveVisibleColumnProperties,
} from '../utils/deriveVisibleColumnConfig';
import { isLookupColumnTargetResolved } from '../utils/lookupColumn';

/** Raw property-type schema entry shape returned by the property-types endpoint. */
interface RawPropertyTypeSchema {
  property_type_name: string;
}

/**
 * Hook that provides prefetch handlers for CRM list pages.
 *
 * Prefetches:
 * - Object list infinite query — warms the same unified list-key cache entry
 *   (`crmQueryKeys.objectsListUnified(..., 'infinite', 'ids', ...)`) that the
 *   list page renders from, so opening the list after a hover serves from
 *   cache. Pass `defaultFilters` matching the target list page's
 *   `config.defaultFilters` sort/order so the prefetch warms the exact same key.
 * - Property schema query (has 5min staleTime - prefetchQuery handles staleness internally)
 *
 * Note: Skips association types as they have staleTime: Infinity (cached permanently).
 *
 * @param orgId - The organization ID
 * @param entityType - The entity type name (ahoy_contact, ahoy_company, ahoy_deal)
 * @param defaultFilters - Optional sort/order to match the target list page's config.defaultFilters
 * @param columns - Optional visible columns for the target list page's config, used to derive
 *   the `properties` filter so the prefetched cache key matches the list page's initial render.
 *   For `kind: 'lookup'` columns, the hook opportunistically checks the query cache for
 *   already-warm association-type / property-schema / object-type data (the same data the list
 *   page's schema hooks populate) and, when all three are present, excludes degraded lookup
 *   columns exactly as the list page would (see `isLookupColumnTargetResolved`). This never
 *   triggers a new fetch — when that data isn't warm yet, a degraded lookup column is passed
 *   through unfiltered (see the bounded-gap note above `include_associations` below).
 * @returns Event handlers for prefetch triggers (onMouseEnter, onMouseLeave, onMouseDown)
 *
 * @example
 * ```tsx
 * const prefetchHandlers = usePrefetchCRMList(orgId, 'ahoy_contact', { sort: 'ahoy_created_at', order: 'desc' }, contactListConfig.columns);
 *
 * <NavItem onPrefetch={prefetchHandlers}>Contacts</NavItem>
 * ```
 */
export function usePrefetchCRMList(
  orgId: number | undefined,
  entityType: EntityTypeName,
  defaultFilters?: Pick<CrmObjectFilters, 'sort' | 'order'>,
  columns?: ColumnConfig[]
): PrefetchHandlers {
  const queryClient = useQueryClient();

  const prefetchFn = useCallback(async () => {
    if (!orgId) {
      return;
    }

    // Prefetch property schema first (has 5min staleTime - prefetchQuery handles staleness
    // internally) so its result can derive `properties` for the list query below.
    const propertySchemaKey = ['propertySchema', orgId, entityType];
    await queryClient.prefetchQuery({
      queryKey: propertySchemaKey,
      queryFn: async () => {
        const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/?entity_type_name=${encodeURIComponent(entityType)}`;
        const response = await fetchWithDefaults(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch property schema: ${response.statusText}`
          );
        }

        return response.json();
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const rawSchema =
      queryClient.getQueryData<RawPropertyTypeSchema[]>(propertySchemaKey);
    const names = new Set<string>(
      (rawSchema ?? []).map(prop => prop.property_type_name)
    );
    const properties = columns
      ? deriveVisibleColumnProperties(columns, names)
      : [];

    // Opportunistic, zero-fetch narrowing: if the association-type,
    // property-schema, and object-type data the list page's schema hooks
    // populate (useAssociationTypes, useAllPropertySchemasByType,
    // useObjectTypes) already happen to be warm in the cache — e.g. the user
    // already visited a list page this session — exclude degraded lookup
    // columns exactly as the list page would via `isLookupColumnTargetResolved`.
    // This ONLY reads from the cache (`getQueryData`) — it never issues a new
    // network request: if any of the three is missing or empty, `columns` is
    // passed through unchanged and the known gap below still applies.
    let effectiveColumns = columns;
    if (columns) {
      try {
        const associationTypes = queryClient.getQueryData<AssociationType[]>([
          'association-types',
          orgId,
        ]);
        const rawAllPropertyTypes = queryClient.getQueryData<
          PropertyTypeSchema[]
        >(['filterSchema', 'allPropertyTypes', String(orgId ?? 0)]);
        const objectTypes = queryClient.getQueryData<CrmObjectTypeSchema[]>(
          entityTypesQueryKey(orgId)
        );

        if (
          associationTypes?.length &&
          rawAllPropertyTypes?.length &&
          objectTypes?.length
        ) {
          const schemasByType: Record<
            string,
            Record<string, PropertyTypeSchema>
          > = {};
          for (const prop of rawAllPropertyTypes) {
            (schemasByType[prop.entity_type_name] ??= {})[
              prop.property_type_name
            ] = prop;
          }
          effectiveColumns = columns.filter(
            column =>
              column.kind !== 'lookup' ||
              isLookupColumnTargetResolved(
                column,
                associationTypes,
                schemasByType,
                objectTypes
              )
          );
        }
      } catch {
        // Malformed/unexpected cache shape must never crash the prefetch or
        // block navigation — fall back to the unfiltered columns below.
        effectiveColumns = columns;
      }
    }

    // Warm the infinite query cache the list page renders from. Spread
    // defaultFilters (sort/order) and the derived properties so the key
    // matches the list page's initial-render cache entry. The association
    // projection MUST be derived with the same helper the list page uses
    // (deriveVisibleColumnAssociations) or the prefetch warms a different
    // cache key than the page reads.
    //
    // Bounded gap: the opportunistic check above only narrows `effectiveColumns`
    // when association-type/property-schema/object-type data is already warm in
    // the cache. This hook deliberately never fetches that data itself (to avoid
    // extra fetches — see "Skipping association types" below), so when it isn't
    // warm yet, a degraded lookup column in `columns` is passed through
    // unfiltered and the prefetch's `include_associations` projection can diverge
    // from what the list page derives, producing a mismatched cache key. The
    // consequence is bounded: this only costs a redundant fetch when the list
    // page mounts (React Query cache miss), never incorrect or leaked data,
    // because the list page always re-derives and re-fetches its own projection
    // independently. Do not "fix" this by unconditionally filtering `kind:
    // 'lookup'` columns out of the prefetch projection — that would guarantee a
    // cache-key mismatch for the common (non-degraded) case, strictly worse than
    // this rare, transient window.
    await queryClient.prefetchInfiniteQuery(
      crmObjectListInfiniteQueryOptions(
        orgId,
        entityType,
        {
          ...defaultFilters,
          page_size: 50,
          ...(properties.length > 0 ? { properties } : {}),
        },
        {
          include_associations: effectiveColumns
            ? deriveVisibleColumnAssociations(effectiveColumns)
            : undefined,
        }
      )
    );

    // Note: Skipping association types as they have staleTime: Infinity
    // and are cached permanently after first load
  }, [queryClient, orgId, entityType, defaultFilters, columns]);

  return usePrefetchOnIntent(prefetchFn);
}
